import React, { useRef, useState, useEffect } from 'react';
import { FiX, FiDownload, FiSquare, FiPrinter } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { format } from 'date-fns';
import { extractLogoColors, getDefaultColorPalette, lightenColor, darkenColor } from '../utils/colorExtraction';

const StudentIDModal = ({ isOpen, onClose, student, school, classInfo }) => {
  const modalRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrHash, setQrHash] = useState('');
  const [brandedQRPreview, setBrandedQRPreview] = useState(null);
  // Nameless card: same card without the student's name, admission number or gender —
  // only the photo and class on the front.
  const [namelessPreview, setNamelessPreview] = useState(null);
  const [previewVersion, setPreviewVersion] = useState('standard'); // 'standard' | 'nameless'
  const [isNamelessDownloading, setIsNamelessDownloading] = useState(false);
  const [qrSuccess, setQrSuccess] = useState("");
  const [qrError, setQrError] = useState("");
  const [colors, setColors] = useState(null);
  const [studentPhotoImage, setStudentPhotoImage] = useState(null);
  const [schoolLogoImage, setSchoolLogoImage] = useState(null);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);

  // Print-perfect dimensions (CR80 standard)
  const PRINT_SPECS = {
    cardWidthMM: 85.6,
    cardHeightMM: 53.98,
    dpi: 300,
    bleedMM: 3,
    safeZoneMM: 5,
    
    get cardWidthPx() {
      return Math.round((this.cardWidthMM / 25.4) * this.dpi);
    },
    get cardHeightPx() {
      return Math.round((this.cardHeightMM / 25.4) * this.dpi);
    },
    get bleedPx() {
      return Math.round((this.bleedMM / 25.4) * this.dpi);
    },
    get safeZonePx() {
      return Math.round((this.safeZoneMM / 25.4) * this.dpi);
    },
    get totalWidthPx() {
      return this.cardWidthPx + (this.bleedPx * 2);
    },
    get totalHeightPx() {
      return this.cardHeightPx + (this.bleedPx * 2);
    }
  };

  useEffect(() => {
    if (isOpen && student) {
      setNamelessPreview(null);
      setPreviewVersion('standard');
      generateQRCode();
      extractColorsFromLogo();
      loadStudentPhoto();
      loadSchoolLogo();
    }
  }, [isOpen, student]);

  useEffect(() => {
    if (isLogoLoaded && qrCodeDataUrl && !brandedQRPreview) {
      generateBrandedQRCode().then(preview => {
        if (preview) {
          setBrandedQRPreview(preview);
        }
      }).catch(error => {
        console.error('Preview generation error:', error);
      });
    }
  }, [isLogoLoaded, qrCodeDataUrl, brandedQRPreview]);

  const loadStudentPhoto = () => {
    if (!student?.picture?.path) {
      setStudentPhotoImage(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    // Force high-quality image loading
    img.onload = () => {
      // Create a high-resolution canvas version of the image
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Set canvas to at least 2x the image size for quality
      const targetSize = Math.max(img.width, img.height, 1200); // Minimum 1200px for print quality
      const scale = targetSize / Math.max(img.width, img.height);
      
      tempCanvas.width = img.width * scale;
      tempCanvas.height = img.height * scale;
      
      // Enable high-quality rendering
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';
      
      // Draw scaled image
      tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);
      
      // Create new high-res image from canvas
      const highResImg = new Image();
      highResImg.onload = () => {
        setStudentPhotoImage(highResImg);
      };
      highResImg.src = tempCanvas.toDataURL('image/png', 1.0);
    };
    
    img.onerror = () => {
      setStudentPhotoImage(null);
    };
    
    // Load original image (append cache buster for fresh load)
    img.src = student.picture.path + (student.picture.path.includes('?') ? '&' : '?') + 'quality=high';
  };

  const loadSchoolLogo = () => {
    if (!school?.picture?.path) {
      setSchoolLogoImage(null);
      setIsLogoLoaded(true);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Create a high-resolution canvas version of the logo
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Ensure logo is at least 800px for print quality
      const targetSize = Math.max(img.width, img.height, 800);
      const scale = targetSize / Math.max(img.width, img.height);
      
      tempCanvas.width = img.width * scale;
      tempCanvas.height = img.height * scale;
      
      // Enable high-quality rendering
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';
      
      // Draw scaled logo
      tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);
      
      // Create new high-res image from canvas
      const highResImg = new Image();
      highResImg.onload = () => {
        setSchoolLogoImage(highResImg);
        setIsLogoLoaded(true);
      };
      highResImg.onerror = () => {
        setSchoolLogoImage(null);
        setIsLogoLoaded(true);
      };
      highResImg.src = tempCanvas.toDataURL('image/png', 1.0);
    };
    
    img.onerror = () => {
      setSchoolLogoImage(null);
      setIsLogoLoaded(true);
    };
    
    // Load original logo (append cache buster for fresh load)
    img.src = school.picture.path + (school.picture.path.includes('?') ? '&' : '?') + 'quality=high';
  };

  const extractColorsFromLogo = async () => {
    if (!school?.picture?.path) {
      setColors(getDefaultColorPalette());
      return;
    }

    try {
      const extractedColors = await extractLogoColors(school.picture.path);
      setColors(extractedColors);
    } catch (error) {
      setColors(getDefaultColorPalette());
    }
  };

  const isQRReady = qrCodeDataUrl.length > 0;

  if (!isOpen || !student) return null;

  const generateQRCode = async () => {
    if (!student || !school?._id || !classInfo?._id) return;
    
    try {
      setIsGenerating(true);
      setQrError("");
      setQrSuccess("");
      
      const { data } = await userRequest.post(`/children/${school._id}/generate-qr-code/${classInfo._id}`, {
        studentIds: [student._id]
      });
      
      if (data.results && data.results.length > 0 && data.results[0].success) {
        const result = data.results[0];
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(result.qrCodeData)}`;
        
        setQrHash(result.qrCodeData);
        setQrCodeDataUrl(qrCodeUrl);
        setQrSuccess("Student ID QR code generated successfully!");
      } else {
        throw new Error(data.message || "Failed to generate QR code for student");
      }
      
    } catch (error) {
      setQrError(error.response?.data?.message || error.message || "Failed to generate student ID QR code");
    } finally {
      setIsGenerating(false);
    }
  };

  const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const formatAddress = (address) => {
    if (!address) return '';
    
    const parts = [
      address.address_line,
      address.city,
      address.state
    ].filter(part => part && part.trim());
    
    return parts.join(', ') || '';
  };

  const generatePrintReadyCard = async (side = 'both', { nameless = false } = {}) => {
    if (!qrCodeDataUrl || !school || !student) return null;
    
    const colorPalette = colors || getDefaultColorPalette();
    const specs = PRINT_SPECS;

    return new Promise((resolve, reject) => {
      const numCards = side === 'both' ? 2 : 1;
      const spacing = specs.cardWidthPx * 0.15;
      const totalWidth = (specs.totalWidthPx * numCards) + (numCards > 1 ? spacing : 0);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = totalWidth;
      canvas.height = specs.totalHeightPx;
      
      ctx.textBaseline = 'top';
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, totalWidth, specs.totalHeightPx);
      
      const drawCropMarks = (offsetX) => {
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 2;
        const markLength = specs.bleedPx * 0.8;
        const bleed = specs.bleedPx;
        
        // Corner marks
        [[0, 0], [specs.cardWidthPx, 0], [0, specs.cardHeightPx], [specs.cardWidthPx, specs.cardHeightPx]].forEach(([dx, dy]) => {
          const x = offsetX + bleed + dx;
          const y = bleed + dy;
          const xDir = dx === 0 ? -1 : 1;
          const yDir = dy === 0 ? -1 : 1;
          
          ctx.beginPath();
          ctx.moveTo(x + xDir * markLength, y);
          ctx.lineTo(x, y);
          ctx.lineTo(x, y + yDir * markLength);
          ctx.stroke();
        });
      };
      
      const qrImage = new Image();
      qrImage.crossOrigin = 'anonymous';
      qrImage.onload = () => {
        try {
          if (side === 'both' || side === 'front') {
            drawFrontCard(0);
          }
          if (side === 'both' || side === 'back') {
            const backOffset = side === 'both' ? specs.totalWidthPx + spacing : 0;
            drawBackCard(backOffset);
          }
          
          if (side === 'both') {
            drawCropMarks(0);
            drawCropMarks(specs.totalWidthPx + spacing);
            
            // Labels
            ctx.fillStyle = '#475569';
            ctx.font = `bold ${specs.cardHeightPx * 0.04}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText('FRONT', specs.totalWidthPx / 2, specs.bleedPx * 0.3);
            ctx.fillText('BACK', specs.totalWidthPx + spacing + specs.totalWidthPx / 2, specs.bleedPx * 0.3);
            
            // Generation timestamp
            ctx.fillStyle = '#94a3b8';
            ctx.font = `${specs.cardHeightPx * 0.028}px "Segoe UI", Arial, sans-serif`;
            ctx.fillText(`Generated: ${format(new Date(), 'dd/MM/yyyy hh:mm a')}`, 
              totalWidth / 2, specs.totalHeightPx - specs.bleedPx * 0.4);
          } else {
            drawCropMarks(0);
          }
          
          function drawFrontCard(offsetX) {
            const bleed = specs.bleedPx;
            const cardX = offsetX + bleed;
            const cardY = bleed;
            const cardWidth = specs.cardWidthPx;
            const cardHeight = specs.cardHeightPx;
            const cornerRadius = cardWidth * 0.025;
            
            // Card background
            ctx.fillStyle = '#ffffff';
            drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cornerRadius);
            ctx.fill();
            
            // Border
            ctx.strokeStyle = `rgb(${colorPalette.primary[0]}, ${colorPalette.primary[1]}, ${colorPalette.primary[2]})`;
            ctx.lineWidth = 4;
            drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cornerRadius);
            ctx.stroke();
            
            // Header section
            const headerHeight = cardHeight * 0.24;
            ctx.fillStyle = `rgb(${colorPalette.primary[0]}, ${colorPalette.primary[1]}, ${colorPalette.primary[2]})`;
            ctx.fillRect(cardX, cardY, cardWidth, headerHeight);
            
            // School logo
            const logoSize = headerHeight * 0.65;
            const logoX = cardX + cardWidth * 0.04;
            const logoY = cardY + (headerHeight - logoSize) / 2;
            
            if (schoolLogoImage) {
              ctx.save();
              // Set highest quality rendering for logo
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              
              ctx.beginPath();
              ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, Math.PI * 2);
              ctx.clip();
              
              // Draw logo at actual resolution (already upscaled during load)
              ctx.drawImage(schoolLogoImage, logoX, logoY, logoSize, logoSize);
              ctx.restore();
              
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, Math.PI * 2);
              ctx.stroke();
            } else {
              ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
              ctx.beginPath();
              ctx.arc(logoX + logoSize/2, logoY + logoSize/2, logoSize/2, 0, Math.PI * 2);
              ctx.fill();
              
              ctx.fillStyle = '#ffffff';
              ctx.font = `bold ${logoSize * 0.4}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('📚', logoX + logoSize/2, logoY + logoSize/2);
            }
            
            // School name and details
            const textX = logoX + logoSize + cardWidth * 0.035;
            ctx.fillStyle = '#ffffff';
            ctx.font = `bold ${cardHeight * 0.055}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            
            const schoolName = (school?.school_name || 'SCHOOL NAME').toUpperCase();
            ctx.fillText(schoolName, textX, cardY + headerHeight * 0.18);
            
            ctx.font = `${cardHeight * 0.042}px "Segoe UI", Arial, sans-serif`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.fillText('STUDENT ID CARD', textX, cardY + headerHeight * 0.48);
            
            const sessionText = school?.settings?.current_session || `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`;
            ctx.font = `${cardHeight * 0.038}px "Segoe UI", Arial, sans-serif`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fillText(sessionText, textX, cardY + headerHeight * 0.72);
            
            // Vertical "STUDENT IDENTITY CARD" text
            ctx.save();
            ctx.translate(cardX + cardWidth * 0.025, cardY + headerHeight + cardHeight * 0.35);
            ctx.rotate(-Math.PI / 2);
            ctx.fillStyle = `rgb(${colorPalette.primary[0]}, ${colorPalette.primary[1]}, ${colorPalette.primary[2]})`;
            ctx.font = `bold ${cardHeight * 0.042}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('STUDENT IDENTITY CARD', 0, 0);
            ctx.restore();
            
            // Photo section
            const photoWidth = cardWidth * 0.26;
            const photoHeight = cardHeight * 0.55;
            const photoX = cardX + cardWidth * 0.075;
            const photoY = cardY + headerHeight + cardHeight * 0.06;
            
            if (studentPhotoImage) {
              ctx.save();
              // Set highest quality rendering for photo
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              
              drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius * 0.8);
              ctx.clip();
              
              const aspectRatio = studentPhotoImage.width / studentPhotoImage.height;
              let drawWidth = photoWidth;
              let drawHeight = photoHeight;
              let drawX = photoX;
              let drawY = photoY;
              
              if (aspectRatio > (photoWidth / photoHeight)) {
                drawHeight = photoHeight;
                drawWidth = photoHeight * aspectRatio;
                drawX = photoX - (drawWidth - photoWidth) / 2;
              } else {
                drawWidth = photoWidth;
                drawHeight = photoWidth / aspectRatio;
                drawY = photoY - (drawHeight - photoHeight) / 2;
              }
              
              // Draw photo at high resolution (already upscaled during load)
              ctx.drawImage(studentPhotoImage, drawX, drawY, drawWidth, drawHeight);
              ctx.restore();
              
              ctx.strokeStyle = '#e5e7eb';
              ctx.lineWidth = 3;
              drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius * 0.8);
              ctx.stroke();
            } else {
              ctx.fillStyle = '#f3f4f6';
              drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius * 0.8);
              ctx.fill();
              
              ctx.fillStyle = '#9ca3af';
              ctx.font = `${photoHeight * 0.3}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('👤', photoX + photoWidth/2, photoY + photoHeight/2);
            }
            
            // Student information fields
            const infoX = photoX + photoWidth + cardWidth * 0.045;
            const infoStartY = photoY;
            const lineHeight = cardHeight * 0.115;
            
            // Helper to draw field
            const drawField = (label, value, y, isBold = false) => {
              ctx.fillStyle = '#6b7280';
              ctx.font = `bold ${cardHeight * 0.032}px "Segoe UI", Arial, sans-serif`;
              ctx.textAlign = 'left';
              ctx.textBaseline = 'top';
              ctx.fillText(label, infoX, y);
              
              ctx.fillStyle = '#111827';
              ctx.font = `${isBold ? 'bold ' : ''}${cardHeight * 0.045}px "Segoe UI", Arial, sans-serif`;
              ctx.fillText(value, infoX, y + cardHeight * 0.048);
              
              ctx.strokeStyle = '#e5e7eb';
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              ctx.moveTo(infoX, y + cardHeight * 0.095);
              ctx.lineTo(cardX + cardWidth - cardWidth * 0.04, y + cardHeight * 0.095);
              ctx.stroke();
            };
            
            if (nameless) {
              // Only the class, in the name's usual spot
              drawField('CLASS', classInfo?.class_name || 'N/A', infoStartY, true);
            } else {
              const fullName = [student.lastname, student.firstname, student.middlename].filter(Boolean).join(' ').toUpperCase();
              drawField('NAME', fullName, infoStartY, true);
              drawField('ADMISSION NO', student.registration_id || 'N/A', infoStartY + lineHeight);
              drawField('CLASS', classInfo?.class_name || 'N/A', infoStartY + lineHeight * 2);
              drawField('GENDER', student.gender || 'N/A', infoStartY + lineHeight * 3);
            }
            
            // Emergency contact
            const emergencyY = cardY + cardHeight - cardHeight * 0.14;
            ctx.fillStyle = '#dc2626';
            ctx.font = `bold ${cardHeight * 0.038}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText('EMERGENCY', photoX, emergencyY);
            
            ctx.fillStyle = '#111827';
            ctx.font = `${cardHeight * 0.038}px "Segoe UI", Arial, sans-serif`;
            ctx.fillText(school?.phone_number || '08100000088', photoX, emergencyY + cardHeight * 0.052);
            
            // Address footer
            const address = formatAddress(school?.address?.main);
            if (address) {
              ctx.fillStyle = '#6b7280';
              ctx.font = `${cardHeight * 0.03}px "Segoe UI", Arial, sans-serif`;
              ctx.textAlign = 'center';
              ctx.fillText(address, cardX + cardWidth / 2, cardY + cardHeight - cardHeight * 0.033);
            }
          }
          
          function drawBackCard(offsetX) {
            const bleed = specs.bleedPx;
            const cardX = offsetX + bleed;
            const cardY = bleed;
            const cardWidth = specs.cardWidthPx;
            const cardHeight = specs.cardHeightPx;
            const cornerRadius = cardWidth * 0.025;
            
            // Card background
            ctx.fillStyle = '#ffffff';
            drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cornerRadius);
            ctx.fill();
            
            // Border
            ctx.strokeStyle = `rgb(${colorPalette.primary[0]}, ${colorPalette.primary[1]}, ${colorPalette.primary[2]})`;
            ctx.lineWidth = 4;
            drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cornerRadius);
            ctx.stroke();
            
            // Header strip
            const headerHeight = cardHeight * 0.12;
            const headerGradient = ctx.createLinearGradient(cardX, cardY, cardX, cardY + headerHeight);
            headerGradient.addColorStop(0, `rgb(${colorPalette.primary[0]}, ${colorPalette.primary[1]}, ${colorPalette.primary[2]})`);
            headerGradient.addColorStop(1, `rgb(${Math.max(0, colorPalette.primary[0] - 30)}, ${Math.max(0, colorPalette.primary[1] - 30)}, ${Math.max(0, colorPalette.primary[2] - 30)})`);
            ctx.fillStyle = headerGradient;
            ctx.fillRect(cardX, cardY, cardWidth, headerHeight);
            
            // Instructions
            const instructionsY = cardY + headerHeight + cardHeight * 0.05;
            ctx.fillStyle = '#111827';
            ctx.font = `bold ${cardHeight * 0.048}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText('IMPORTANT INSTRUCTIONS', cardX + cardWidth * 0.05, instructionsY);
            
            const instructions = [
              '• This ID Card must be with you at all times both',
              '  in and out of the school. Should you lose it, you',
              '  would be required to get a replacement immediately.',
              '',
              '• This ID Card is the property of: ',
              `  ${(school?.school_name || 'SCHOOL').toUpperCase()}`,
              '  If found, please return to the nearest Police',
              '  Station or the address on this Card.',
              '',
              'Contact: ' + (school?.phone_number || '08100000088'),
              'Email: ' + (school?.school_email || 'abir.itsuki@moongit.com')
            ];
            
            ctx.fillStyle = '#374151';
            ctx.font = `${cardHeight * 0.035}px "Segoe UI", Arial, sans-serif`;
            instructions.forEach((instruction, index) => {
              if (instruction.trim() === '') return;
              ctx.fillText(instruction, cardX + cardWidth * 0.05, instructionsY + cardHeight * 0.08 + (index * cardHeight * 0.048));
            });
            
            // QR Code size
            const qrSize = cardHeight * 0.50;
            const qrX = cardX + cardWidth - qrSize - cardWidth * 0.08;
            const qrY = instructionsY + cardHeight * 0.08;
            
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 2;
            drawRoundedRect(ctx, qrX - cardWidth * 0.02, qrY - cardHeight * 0.02, 
              qrSize + cardWidth * 0.04, qrSize + cardHeight * 0.11, cornerRadius * 0.5);
            ctx.fill();
            ctx.shadowColor = 'transparent';
            
            ctx.save();
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
            ctx.restore();
            
            ctx.fillStyle = '#111827';
            ctx.font = `bold ${cardHeight * 0.042}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText('QR CODE', qrX + qrSize/2, qrY + qrSize + cardHeight * 0.018);
            
            ctx.font = `${cardHeight * 0.032}px "Segoe UI", Arial, sans-serif`;
            ctx.fillStyle = '#6b7280';
            ctx.fillText('Scan for attendance', qrX + qrSize/2, qrY + qrSize + cardHeight * 0.062);
            
            // Principal signature
            const sigX = cardX + cardWidth * 0.05;
            const sigY = cardY + cardHeight - cardHeight * 0.19;
            const sigWidth = cardWidth * 0.25;
            const sigHeight = cardHeight * 0.11;
            
            if (school?.admin_signature?.path) {
              const signatureImage = new Image();
              signatureImage.crossOrigin = 'anonymous';
              signatureImage.onload = () => {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(sigX, sigY, sigWidth, sigHeight);
                ctx.strokeStyle = '#d1d5db';
                ctx.lineWidth = 2;
                ctx.strokeRect(sigX, sigY, sigWidth, sigHeight);
                
                // High-quality signature rendering
                ctx.save();
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(signatureImage, sigX + sigWidth * 0.05, sigY + sigHeight * 0.1, 
                  sigWidth * 0.9, sigHeight * 0.8);
                ctx.restore();
                
                ctx.fillStyle = '#6b7280';
                ctx.font = `${cardHeight * 0.032}px "Segoe UI", Arial, sans-serif`;
                ctx.textAlign = 'center';
                ctx.fillText('Principal Signature', sigX + sigWidth/2, sigY + sigHeight + cardHeight * 0.018);
                
                finalizeCanvas();
              };
              signatureImage.onerror = () => {
                drawSignatureLine();
              };
              signatureImage.src = school.admin_signature.path;
            } else {
              drawSignatureLine();
            }
            
            function drawSignatureLine() {
              ctx.strokeStyle = '#9ca3af';
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(sigX, sigY + sigHeight/2);
              ctx.lineTo(sigX + sigWidth, sigY + sigHeight/2);
              ctx.stroke();
              
              ctx.fillStyle = '#6b7280';
              ctx.font = `${cardHeight * 0.032}px "Segoe UI", Arial, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              ctx.fillText('Principal Signature', sigX + sigWidth/2, sigY + sigHeight + cardHeight * 0.018);
              
              finalizeCanvas();
            }
            
            // Footer
            ctx.fillStyle = '#9ca3af';
            ctx.font = `${cardHeight * 0.028}px "Segoe UI", Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText('Powered by Acceede.com', cardX + cardWidth/2, cardY + cardHeight - cardHeight * 0.028);
          }
          
          function finalizeCanvas() {
            resolve({
              dataUrl: canvas.toDataURL('image/png', 1.0),
              width: canvas.width,
              height: canvas.height,
              dpi: specs.dpi
            });
          }
          
        } catch (error) {
          reject(error);
        }
      };
      
      qrImage.onerror = () => {
        reject(new Error('Failed to load QR code image'));
      };
      
      qrImage.src = qrCodeDataUrl;
    });
  };

  const generateBrandedQRCode = async () => {
    if (!qrCodeDataUrl || !school || !student) return null;
    
    const result = await generatePrintReadyCard('both');
    return result?.dataUrl;
  };

  const handleDownloadPrintReady = async () => {
    if (!qrCodeDataUrl) return;
    
    try {
      setIsDownloading(true);
      
      const bothSides = await generatePrintReadyCard('both');
      
      if (bothSides) {
        const studentName = `${student.firstname}_${student.lastname}`.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        const schoolName = school?.school_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'school';
        const admissionNo = student.registration_id || 'N/A';
        
        const link = document.createElement('a');
        link.href = bothSides.dataUrl;
        link.download = `${studentName}_${admissionNo}_${schoolName}_PRINT_READY_300DPI.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setQrSuccess("Print-ready ID card (300 DPI) downloaded successfully!");
      }
    } catch (error) {
      setQrError("Failed to generate print-ready ID card");
    } finally {
      setIsDownloading(false);
    }
  };

  const showPreviewVersion = async (version) => {
    setPreviewVersion(version);
    if (version === 'nameless' && !namelessPreview) {
      try {
        const result = await generatePrintReadyCard('both', { nameless: true });
        if (result) setNamelessPreview(result.dataUrl);
      } catch (error) {
        console.error('Nameless preview generation error:', error);
      }
    }
  };

  const handleDownloadNameless = async () => {
    if (!qrCodeDataUrl) return;

    try {
      setIsNamelessDownloading(true);

      const card = await generatePrintReadyCard('both', { nameless: true });

      if (card) {
        // The file name still identifies the student so the school can tell files apart;
        // only the printed card is nameless.
        const studentName = `${student.firstname}_${student.lastname}`.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        const schoolName = school?.school_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'school';

        const link = document.createElement('a');
        link.href = card.dataUrl;
        link.download = `${studentName}_${schoolName}_NAMELESS_300DPI.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setQrSuccess("Nameless ID card downloaded successfully!");
      }
    } catch (error) {
      setQrError("Failed to generate nameless ID card");
    } finally {
      setIsNamelessDownloading(false);
    }
  };

  const handleDownloadSingleSide = async (side) => {
    if (!qrCodeDataUrl) return;
    
    try {
      setIsDownloading(true);
      
      const cardData = await generatePrintReadyCard(side);
      
      if (cardData) {
        const studentName = `${student.firstname}_${student.lastname}`.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        const schoolName = school?.school_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'school';
        const admissionNo = student.registration_id || 'N/A';
        
        const link = document.createElement('a');
        link.href = cardData.dataUrl;
        link.download = `${studentName}_${admissionNo}_${schoolName}_${side.toUpperCase()}_300DPI.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setQrSuccess(`${side === 'front' ? 'Front' : 'Back'} side downloaded successfully!`);
      }
    } catch (error) {
      setQrError(`Failed to generate ${side} side`);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Student ID Card - Print Ready</h2>
              <p className="text-sm text-gray-600 mt-1">
                Professional quality • 300 DPI • CR80 Standard (85.6mm × 53.98mm)
              </p>
              {/*<div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded">✓ Print Ready</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">✓ 3mm Bleed</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">✓ Crop Marks</span>
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">✓ High-Res QR Code</span>
                <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded">✓ High-Res Photos</span>
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">✓ High-Res Logo</span>
              </div>*/}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6" ref={modalRef}>
          {qrCodeDataUrl ? (
            <div className="space-y-6">
              {/* Preview Section */}
              {brandedQRPreview && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Print Preview</h3>
                    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-sm">
                      {[
                        { key: 'standard', label: 'Standard' },
                        { key: 'nameless', label: 'Nameless' },
                      ].map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => showPreviewVersion(option.key)}
                          className={`px-3 py-1 rounded-md font-medium transition-colors ${
                            previewVersion === option.key
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {/*<div className="text-xs text-gray-500 space-y-1 text-right">
                      <div>Resolution: 300 DPI (Print Quality)</div>
                      <div>Dimensions: {PRINT_SPECS.cardWidthMM}mm × {PRINT_SPECS.cardHeightMM}mm</div>
                      <div>Format: CR80 Standard with Bleed</div>
                    </div>*/}
                  </div>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    {previewVersion === 'nameless' && !namelessPreview ? (
                      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-600 mr-3"></div>
                        Preparing nameless preview...
                      </div>
                    ) : (
                      <img
                        src={previewVersion === 'nameless' ? namelessPreview : brandedQRPreview}
                        alt={previewVersion === 'nameless' ? 'Nameless Student ID Card Print Preview' : 'Student ID Card Print Preview'}
                        className="w-full h-auto mx-auto"
                        style={{ maxWidth: '100%', imageRendering: 'high-quality' }}
                      />
                    )}
                  </div>
                  {/*<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <FiPrinter className="w-5 h-5 text-blue-600 mt-0.5" />
                      </div>
                      <div className="ml-3 text-sm text-blue-800">
                        <p className="font-medium mb-1">Printing Instructions:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Print at 100% scale (do not resize)</li>
                          <li>Use high-quality card stock (minimum 300gsm)</li>
                          <li>Cut along crop marks for perfect alignment</li>
                          <li>Consider laminating for durability</li>
                          <li>Professional print shops recommended for best results</li>
                        </ul>
                      </div>
                    </div>
                  </div>*/}
                </div>
              )}

              {/* Student Information */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-xs font-medium text-gray-500">Student Name:</span>
                  <p className="text-sm font-semibold text-gray-800">
                    {student.firstname} {student.lastname}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Admission No:</span>
                  <p className="text-sm font-semibold text-gray-800">
                    {student.registration_id || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Class:</span>
                  <p className="text-sm font-semibold text-gray-800">
                    {classInfo?.class_name || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Generated:</span>
                  <p className="text-sm font-semibold text-gray-800">
                    {format(new Date(), 'dd/MM/yyyy hh:mm a')}
                  </p>
                </div>
              </div>
            
              {/* Download Options */}
              <div className="space-y-3">
                <h3 className="text-md font-semibold text-gray-800">Download Options</h3>
                
                {/* Primary Download - Both Sides */}
                <button
                  onClick={handleDownloadPrintReady}
                  disabled={isDownloading}
                  className="w-full group relative inline-flex items-center justify-center px-6 py-4 border-2 border-green-500 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                  ) : (
                    <FiDownload className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200" />
                  )}
                  <div className="text-left">
                    <div className="font-bold">Download Complete ID Card (Recommended)</div>
                    <div className="text-xs font-normal opacity-90">Both sides • 300 DPI • Ready to print</div>
                  </div>
                </button>

                {/* Nameless Download - photo and class only */}
                <button
                  onClick={handleDownloadNameless}
                  disabled={isNamelessDownloading || isDownloading}
                  className="w-full group inline-flex items-center justify-center px-6 py-4 border-2 border-gray-300 text-base font-semibold rounded-xl text-gray-800 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isNamelessDownloading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent mr-3"></div>
                  ) : (
                    <FiDownload className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-200" />
                  )}
                  <div className="text-left">
                    <div className="font-bold">Download Nameless ID Card</div>
                    <div className="text-xs font-normal text-gray-500">
                      Photo and class only • no name, admission number or gender • same QR code
                    </div>
                  </div>
                </button>

                {/* Individual Sides Download
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleDownloadSingleSide('front')}
                    disabled={isDownloading}
                    className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiDownload className="w-4 h-4 mr-2" />
                    Front Only
                  </button>
                  <button
                    onClick={() => handleDownloadSingleSide('back')}
                    disabled={isDownloading}
                    className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiDownload className="w-4 h-4 mr-2" />
                    Back Only
                  </button>
                </div> */}
              </div>

              {/* Technical Specifications
              <details className="border border-gray-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer hover:bg-gray-50 font-medium text-sm text-gray-700">
                  Technical Specifications
                </summary>
                <div className="px-4 py-3 border-t border-gray-200 text-xs space-y-2 text-gray-600">
                  <div className="grid grid-cols-2 gap-2">
                    <div><strong>Card Standard:</strong> CR80 (Credit Card Size)</div>
                    <div><strong>Dimensions:</strong> 85.6mm × 53.98mm</div>
                    <div><strong>Resolution:</strong> 300 DPI</div>
                    <div><strong>Bleed:</strong> 3mm all sides</div>
                    <div><strong>Safe Zone:</strong> 5mm from edge</div>
                    <div><strong>Pixel Dimensions:</strong> {PRINT_SPECS.cardWidthPx} × {PRINT_SPECS.cardHeightPx}px</div>
                    <div><strong>File Format:</strong> PNG (High Quality)</div>
                    <div><strong>Color Space:</strong> RGB (convert to CMYK for offset printing)</div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="font-medium mb-1">Printing Recommendations:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Card Stock: 300-350gsm (minimum)</li>
                      <li>Finish: Matte or Glossy lamination recommended</li>
                      <li>Corners: Optional rounded corners (3-5mm radius)</li>
                      <li>For commercial printing, convert to CMYK color space</li>
                    </ul>
                  </div>
                </div>
              </details> */}
            </div>
          ) : (
            <div className="text-center py-12">
              <FiSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-2">QR code data not available</p>
              {isGenerating && (
                <div className="mt-4">
                  <div className="inline-block w-8 h-8 border-3 border-gray-300 border-t-green-600 rounded-full animate-spin" aria-hidden="true"></div>
                  <p className="text-sm text-gray-500 mt-3">Generating high-resolution QR code...</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Success/Error Notifications */}
        {qrSuccess && (
          <div className="mx-6 mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-green-800 font-medium">{qrSuccess}</p>
            </div>
          </div>
        )}
        {qrError && (
          <div className="mx-6 mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800 font-medium">{qrError}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentIDModal;